import { SAVE_VERSION, type PlayerSave } from '../PlayerSave.schema';

/**
 * System migracji save'a.
 *
 * Każda zmiana w `PlayerSave` schema = bump `SAVE_VERSION` + dopisanie funkcji
 * migracyjnej do `migrations`. Funkcja dostaje save w wersji `n-1`, zwraca
 * save w wersji `n`.
 *
 * Przykład (przyszłość):
 *
 *   // gdy SAVE_VERSION = 2:
 *   const migrations: Record<number, MigrationFn> = {
 *     2: (saveV1) => ({
 *       ...saveV1,
 *       newField: 'default value',
 *       version: 2,
 *     }),
 *   };
 *
 * `applyMigrations` itruje od `save.version` do `SAVE_VERSION`, wywołując
 * po kolei każdą migrację. Brak migracji w łańcuchu = rzuca error.
 *
 * W MVP nie mamy żadnych migracji (SAVE_VERSION = 1, save tworzony od razu
 * w aktualnej wersji). Plik istnieje żeby infrastruktura była gotowa.
 */

type MigrationFn = (save: Record<string, unknown>) => Record<string, unknown>;

function legacyCoinValueToString(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value) && Number.isInteger(value) && value >= 0) {
    // Rozwija również zapis wykładniczy (`1e+21`) ze starego JSON do cyfr.
    return BigInt(value).toString();
  }
  return String(value ?? 0);
}

const migrations: Record<number, MigrationFn> = {
  /**
   * v1 → v2 (sesja 30): dodanie onboarding flags `starterPetsChosen` i
   * `hasSeenFabTutorial`.
   *
   * Strategia: heurystyka `hasRealProgress` — gracz który miał inventory,
   * equipped pety LUB zhatchował cokolwiek wcześniej już PRZESZEDŁ onboarding
   * w v1. Bezpieczne ustawić obie flagi na `true`.
   *
   * Edge case: fresh v1 save z `avatarSelected=false` (gracz nigdy nie wybrał
   * avatara) — brak progressu, oba flags zostają `false` → przejdzie full
   * onboarding (avatar + nowy starter selector).
   *
   * **Nie używamy `eggs.length > 0`** jako sygnału ukończonego onboarding —
   * fresh save w v1 miał 3 starter eggs PRZED wyborem avatara (Codex sesja 30 review).
   */
  2: (saveV1) => {
    const flags = (saveV1.flags as Record<string, boolean | number> | undefined) ?? {};
    const inventory = saveV1.inventory as unknown[] | undefined;
    const equipped = saveV1.equipped as unknown[] | undefined;
    const stats = saveV1.stats as { petsHatched?: number } | undefined;
    const hasRealProgress =
      (inventory?.length ?? 0) > 0
      || (equipped?.length ?? 0) > 0
      || (stats?.petsHatched ?? 0) > 0;

    // Sesja 30 POST review MED (Codex): no-progress v1 save (stuck pre-avatar)
    // miał 3 starter eggs z pre-30 `STARTER_SEED`. Po migracji do v2 gracz
    // przejdzie nowy onboarding (avatar + starter selector) → dostanie 2 pety
    // + 3 stare eggi = 5 entities total. Niespójne z fresh v2 save (2 pety +
    // 1 egg = 3 entities). Normalizujemy do 1 starter egg żeby flow był
    // identyczny dla wszystkich nowych graczy (fresh v2 LUB stuck-v1-migrated).
    let normalizedEggs = saveV1.eggs;
    if (!hasRealProgress && Array.isArray(saveV1.eggs) && saveV1.eggs.length > 1) {
      // Bierz pierwszy egg (FIFO) — zwykle wszystkie 3 są identyczne starter,
      // ale gdyby były różne, pierwszy jest najbardziej "starter-like".
      normalizedEggs = saveV1.eggs.slice(0, 1);
    }

    // Sesja 30 POST review HIGH (Codex): MUSIMY też ustawić `avatarSelected=true`
    // dla istniejących graczy z progresem. Save sprzed wprowadzenia
    // `flags.avatarSelected` (sesja 18, pre-30) mógł nie mieć tego klucza, ale
    // gracz wyraźnie grał (inventory/equipped/petsHatched > 0) → już przeszedł
    // jakiś avatar flow. Bez tego strict hydratacja `=== true` w main.tsx
    // wymusiłaby ponowne AvatarSelectorModal.
    return {
      ...saveV1,
      eggs: normalizedEggs,   // trimmed do 1 dla stuck-v1-pre-avatar saves
      flags: {
        ...flags,
        avatarSelected: hasRealProgress ? true : (flags.avatarSelected === true),
        starterPetsChosen: hasRealProgress ? true : (flags.starterPetsChosen === true),
        hasSeenFabTutorial: hasRealProgress ? true : (flags.hasSeenFabTutorial === true),
      },
      version: 2,
    };
  },

  /**
   * v2 → v3 (mikstury): dodanie `potions` (plecak mikstur) i `activeEffects`
   * (aktywne buffy czasowe). Oba puste dla istniejących graczy — nikt nie miał
   * jeszcze mikstur. Wartości autorytatywne i tak pochodzą z serwera (kolumny
   * `potions`/`active_effects` z domyślnym `{}`), to tylko spójność kształtu
   * lokalnego save'a (IDB).
   */
  3: (saveV2) => ({
    ...saveV2,
    potions: saveV2.potions ?? {},
    activeEffects: saveV2.activeEffects ?? {},
    version: 3,
  }),

  /**
   * v3 → v4 (eventy): dodanie `eventCurrencies` — generycznego portfela walut
   * eventowych (id→ilość). Pusty dla istniejących graczy: nikt nie miał jeszcze
   * waluty eventowej (pierwszy event dopiero powstaje). Wartość autorytatywna
   * pochodzi z serwera (kolumna `event_currencies` z domyślnym `{}`); to tylko
   * spójność kształtu lokalnego save'a (IDB) — analogicznie do v2→v3 potions.
   */
  4: (saveV3) => ({
    ...saveV3,
    eventCurrencies: saveV3.eventCurrencies ?? {},
    version: 4,
  }),

  /**
   * v4 → v5: saldo i łączny zarobek przechodzą z JS number na decimal string.
   * Stare wartości były ograniczone do bezpiecznego zakresu, więc String()
   * zachowuje je dokładnie. Od v5 żadna z tych wartości nie wraca do number.
   */
  5: (saveV4) => {
    const currencies = (saveV4.currencies ?? {}) as Record<string, unknown>;
    const stats = (saveV4.stats ?? {}) as Record<string, unknown>;
    return {
      ...saveV4,
      currencies: {
        ...currencies,
        coins: legacyCoinValueToString(currencies.coins),
      },
      stats: {
        ...stats,
        coinsEarnedTotal: legacyCoinValueToString(stats.coinsEarnedTotal),
      },
      version: 5,
    };
  },
};

/**
 * Sprowadza save do aktualnej wersji `SAVE_VERSION`.
 *
 * Defensywy:
 *  - Wersja wejściowa musi być nieujemnym integer'em (inaczej throws).
 *  - Po każdej migracji **asercja** `result.version === expectedNext` — jeśli
 *    migracja zwróci save z inną wersją (bug w migration fn), throws zamiast
 *    nieskończonej pętli.
 *
 * Throws jeśli brakuje którejś z migracji po drodze.
 */
export function applyMigrations(save: Record<string, unknown>): PlayerSave {
  let current = save;

  // Walidacja wersji wejściowej
  const rawVersion = current.version;
  if (typeof rawVersion !== 'number' || !Number.isInteger(rawVersion) || rawVersion < 0) {
    throw new Error(
      `[Migrations] Save ma nieprawidłową wersję: ${String(rawVersion)} ` +
      `(oczekiwany nieujemny integer).`
    );
  }
  let version = rawVersion;

  while (version < SAVE_VERSION) {
    const next = version + 1;
    const migrate = migrations[next];
    if (!migrate) {
      throw new Error(
        `[Migrations] Brak migracji ${version} → ${next}. ` +
        `Dopisz wpis w src/save/migrations/index.ts.`
      );
    }

    current = migrate(current);

    // Asercja exact version — zapobiega infinite loop jeśli migration ma bug
    const newVersion = current.version;
    if (newVersion !== next) {
      throw new Error(
        `[Migrations] Migracja ${version} → ${next} zwróciła save w wersji ` +
        `${String(newVersion)}. Migracja musi ustawić .version = ${next}.`
      );
    }
    version = next;
  }

  return current as unknown as PlayerSave;
}
